import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, Copy, Loader2, Mic, RotateCcw, X } from 'lucide-react';
import { useDictation, DictationResult } from '../../hooks/useDictation';

/**
 * Floating dictation panel.
 *
 * Global rather than embedded in one editor because dictation starts by voice
 * ("hey clinic, start dictation") and the provider may be anywhere in the app
 * when they say it. Mounted once, near the voice controls.
 *
 * The panel shows the transcript as it accumulates. That is the whole point:
 * a provider talking for four minutes needs to see that they are being heard,
 * and needs the words in front of them if recognition drops something.
 */

interface DictationPanelProps {
  onApply?: (result: DictationResult) => void;
}

const COLORS = {
  panel: '#16325B',
  border: '#28527A',
  text: '#f9fafb',
  label: '#9ca3af',
  accent: '#6CA6C1',
  danger: '#ef4444',
  warning: '#D4A017',
  success: '#10b981',
};

function elapsedLabel(startedAt: number | null, now: number): string {
  if (!startedAt) return '0:00';
  const seconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

const DictationPanel: React.FC<DictationPanelProps> = ({ onApply }) => {
  const { isDictating, isProcessing, status, transcript, startedAt, error, result, stop, cancel, retry } =
    useDictation();

  const [now, setNow] = useState(Date.now());
  const [copied, setCopied] = useState(false);
  const transcriptRef = useRef<HTMLDivElement>(null);

  // Tick only while recording — no timer running in the background otherwise.
  useEffect(() => {
    if (!isDictating) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isDictating]);

  // Keep the newest speech visible without stealing focus.
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [transcript]);

  if (status === 'idle' && !result) return null;

  const copyNote = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked; the text is on screen and selectable anyway.
    }
  };

  return (
    <aside
      role="region"
      aria-label="Dictation"
      style={{
        position: 'fixed',
        right: '20px',
        bottom: '20px',
        width: 'min(420px, calc(100vw - 40px))',
        maxHeight: '70vh',
        overflowY: 'auto',
        background: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '12px',
        boxShadow: '0 18px 48px rgba(0,0,0,0.5)',
        color: COLORS.text,
        zIndex: 1000,
        padding: '16px',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        {isDictating ? (
          <span
            aria-hidden="true"
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: COLORS.danger,
              flexShrink: 0,
            }}
          />
        ) : (
          <Mic size={16} aria-hidden="true" style={{ color: COLORS.accent }} />
        )}

        <strong style={{ fontSize: '14px', flex: 1 }}>
          {isDictating && `Dictating · ${elapsedLabel(startedAt, now)}`}
          {isProcessing && 'Writing up your note…'}
          {status === 'error' && 'Dictation not saved'}
          {status === 'idle' && result && 'Note ready'}
        </strong>

        <button
          type="button"
          onClick={cancel}
          aria-label="Close dictation panel and discard"
          style={{
            background: 'none',
            border: 'none',
            color: COLORS.label,
            cursor: 'pointer',
            display: 'flex',
            padding: '2px',
          }}
        >
          <X size={16} aria-hidden="true" />
        </button>
      </header>

      {/* Live announcement so a provider using a screen reader knows the state. */}
      <p role="status" aria-live="polite" className="sr-only" style={{ position: 'absolute', left: '-10000px' }}>
        {isDictating ? 'Dictation is recording' : isProcessing ? 'Processing dictation' : ''}
      </p>

      {(isDictating || isProcessing) && (
        <>
          <div
            ref={transcriptRef}
            style={{
              maxHeight: '180px',
              overflowY: 'auto',
              background: 'rgba(0,0,0,0.25)',
              borderRadius: '8px',
              padding: '10px',
              fontSize: '13px',
              lineHeight: 1.6,
              color: transcript ? COLORS.text : COLORS.label,
              fontStyle: transcript ? 'normal' : 'italic',
            }}
          >
            {transcript || 'Listening… start talking through the session.'}
          </div>

          {isDictating && (
            <p style={{ margin: '10px 0 0', fontSize: '12px', color: COLORS.label }}>
              Say <strong style={{ color: COLORS.text }}>“stop dictation”</strong> when you're
              finished, or <strong style={{ color: COLORS.text }}>“cancel dictation”</strong> to
              discard.
            </p>
          )}
        </>
      )}

      {isDictating && (
        <button
          type="button"
          onClick={stop}
          style={{
            marginTop: '12px',
            width: '100%',
            padding: '10px',
            borderRadius: '8px',
            border: 'none',
            background: COLORS.accent,
            color: '#0A2540',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Stop dictation
        </button>
      )}

      {isProcessing && (
        <div
          style={{
            marginTop: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: COLORS.label,
            fontSize: '13px',
          }}
        >
          <Loader2 size={14} aria-hidden="true" />
          Structuring into sections…
        </div>
      )}

      {status === 'error' && (
        <div style={{ marginTop: '4px' }}>
          <p style={{ margin: '0 0 8px', fontSize: '13px', color: COLORS.danger }}>{error}</p>
          {/* The transcript survives a failure — never make them dictate twice. */}
          <div
            style={{
              maxHeight: '140px',
              overflowY: 'auto',
              background: 'rgba(0,0,0,0.25)',
              borderRadius: '8px',
              padding: '10px',
              fontSize: '13px',
              lineHeight: 1.6,
              marginBottom: '10px',
            }}
          >
            {transcript}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={retry}
              style={{
                flex: 1,
                padding: '9px',
                borderRadius: '8px',
                border: 'none',
                background: COLORS.accent,
                color: '#0A2540',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              <RotateCcw size={13} aria-hidden="true" />
              Try again
            </button>
            <button
              type="button"
              onClick={copyNote}
              style={{
                padding: '9px 12px',
                borderRadius: '8px',
                border: `1px solid ${COLORS.border}`,
                background: 'transparent',
                color: COLORS.accent,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Copy transcript
            </button>
          </div>
        </div>
      )}

      {result && status === 'idle' && (
        <div>
          {result.warnings.length > 0 && (
            <ul
              style={{
                margin: '0 0 12px',
                padding: '10px 10px 10px 26px',
                borderRadius: '8px',
                background: 'rgba(212,160,23,0.12)',
                border: `1px solid ${COLORS.warning}`,
                fontSize: '12px',
              }}
            >
              {result.warnings.map((warning, index) => (
                <li key={index} style={{ marginBottom: '3px' }}>
                  {warning}
                </li>
              ))}
            </ul>
          )}

          {Object.entries(result.sections).map(([section, text]) => (
            <div key={section} style={{ marginBottom: '10px' }}>
              <div
                style={{
                  color: COLORS.label,
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  marginBottom: '3px',
                }}
              >
                {section}
              </div>
              <div
                style={{
                  fontSize: '13px',
                  lineHeight: 1.5,
                  color: text ? COLORS.text : COLORS.label,
                  fontStyle: text ? 'normal' : 'italic',
                }}
              >
                {text || 'Not dictated'}
              </div>
            </div>
          ))}

          {/* Content that did not fit a section is shown, never dropped. */}
          {result.unassigned && (
            <div
              style={{
                marginTop: '10px',
                padding: '10px',
                borderRadius: '8px',
                border: `1px dashed ${COLORS.border}`,
              }}
            >
              <div style={{ color: COLORS.warning, fontSize: '11px', marginBottom: '3px' }}>
                <AlertTriangle size={11} aria-hidden="true" /> Not placed in a section
              </div>
              <div style={{ fontSize: '13px', lineHeight: 1.5 }}>{result.unassigned}</div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
            {onApply && (
              <button
                type="button"
                onClick={() => onApply(result)}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '8px',
                  border: 'none',
                  background: COLORS.success,
                  color: '#04231a',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Add to note
              </button>
            )}
            <button
              type="button"
              onClick={copyNote}
              style={{
                flex: onApply ? '0 0 auto' : 1,
                padding: '10px 12px',
                borderRadius: '8px',
                border: `1px solid ${COLORS.border}`,
                background: 'transparent',
                color: copied ? COLORS.success : COLORS.accent,
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>

          <p style={{ margin: '10px 0 0', fontSize: '11px', color: COLORS.label }}>
            Draft from dictation — review before signing.
          </p>
        </div>
      )}
    </aside>
  );
};

export default DictationPanel;
